/**
 * 결제 및 정산 정보 — 하위 메뉴별 페이지
 *  1) 크레딧 결제   (사업자 → 멍투데이, HIST_PURCHASE_CREDIT)
 *  2) 온라인 결제   (보호자 → 사업자, HIST_PURCHASE_TICKET / ONLINE)
 *  3) 오프라인 결제 (수동 발급, HIST_PURCHASE_TICKET / OFFLINE — 크레딧으로 과금)
 */

import { usePayments } from '../../hooks/usePayments';
import { useCreditPayments } from '../../hooks/useCreditPayments';
import { PaymentStats } from './PaymentStats';
import { PaymentList } from './PaymentList';
import { TicketPaymentFilters } from './TicketPaymentFilters';
import { CreditPaymentFilters } from './CreditPaymentFilters';
import { CreditPaymentList } from './CreditPaymentList';
import type { ComponentProps } from 'react';
import type { PaymentType } from '../../types/payment';

/* ─── 1) 크레딧 결제 ─── */
export function CreditPaymentPage() {
  const {
    payments, pagination, statistics, isLoading, isFetching, error,
    page, size, filters, setPage, setFilters, setSize, refetch,
  } = useCreditPayments();

  return (
    <div className="space-y-4">
      <PageHeader
        title="크레딧 결제"
        subtitle="사업자 → 멍투데이 크레딧 충전(PG) 결제 내역입니다."
      />

      {statistics && <PaymentStats statistics={statistics} variant="default" />}

      <CreditPaymentFilters
        filters={filters}
        onFilterChange={setFilters}
        pageSize={size}
        onPageSizeChange={(nextSize) => { setSize(nextSize); setPage(1); }}
      />

      {isLoading ? (
        <LoadingCard label="크레딧 결제 내역을 불러오는 중..." />
      ) : error ? (
        <ErrorCard error={error} />
      ) : (
        <>
          <div className="relative">
            <CreditPaymentList payments={payments} onCancelled={() => refetch()} />
            {isFetching && <FetchingOverlay />}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationBar
              page={page}
              totalPages={pagination.totalPages}
              totalElements={pagination.totalElements}
              pageSize={pagination.pageSize}
              onChange={setPage}
            />
          )}
        </>
      )}
    </div>
  );
}

/* ─── 2) 온라인 결제 ─── */
export function OnlinePaymentPage() {
  return (
    <TicketPaymentPage
      paymentType="ONLINE"
      title="온라인 결제"
      subtitle="보호자 → 사업자 이용권 온라인(PG) 결제 내역입니다."
      loadingLabel="온라인 결제 내역을 불러오는 중..."
    />
  );
}

/* ─── 3) 오프라인 결제 (수동 발급) ─── */
export function OfflinePaymentPage() {
  return (
    <TicketPaymentPage
      paymentType="OFFLINE"
      title="오프라인 결제"
      subtitle="수동 발급 이용권 내역입니다. 발급 시 사업자 크레딧으로 과금되며 별도 정산은 없습니다."
      loadingLabel="오프라인 발급 내역을 불러오는 중..."
      statsLabels={{
        total: '총 발급 금액',
        count: '발급 건수',
        cancel: '취소/회수',
        net: '순 발급 금액',
        netSub: '발급 − 취소·회수',
      }}
      statusLabels={{ PAID: '발급' }}
    />
  );
}

/* ─── 이용권 결제 공용 본문 (온라인/오프라인) ─── */
function TicketPaymentPage({
  paymentType,
  title,
  subtitle,
  loadingLabel,
  statsLabels,
  statusLabels,
}: {
  paymentType: PaymentType;
  title: string;
  subtitle: string;
  loadingLabel: string;
  statsLabels?: ComponentProps<typeof PaymentStats>['labels'];
  statusLabels?: ComponentProps<typeof TicketPaymentFilters>['statusLabels'];
}) {
  // 필터/목록이 같은 문구를 쓰도록 상태 라벨을 함께 전달 (오프라인: 결제 → 발급)
  const {
    payments, pagination, statistics, isLoading, isFetching, error,
    page, size, filters, setPage, setFilters, setSize,
  } = usePayments(1, 10, paymentType);

  return (
    <div className="space-y-4">
      <PageHeader title={title} subtitle={subtitle} />

      {statistics && (
        <PaymentStats statistics={statistics} variant="default" labels={statsLabels} />
      )}

      <TicketPaymentFilters
        filters={filters}
        onFilterChange={setFilters}
        pageSize={size}
        onPageSizeChange={(nextSize) => { setSize(nextSize); setPage(1); }}
        statusLabels={statusLabels}
      />

      {isLoading ? (
        <LoadingCard label={loadingLabel} />
      ) : error ? (
        <ErrorCard error={error} />
      ) : (
        <>
          <div className="relative">
            <PaymentList
              payments={payments}
              hideTypeColumn
              hideCreditColumn={paymentType === 'ONLINE'}
              statusLabels={statusLabels}
            />
            {isFetching && <FetchingOverlay />}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationBar
              page={page}
              totalPages={pagination.totalPages}
              totalElements={pagination.totalElements}
              pageSize={pagination.pageSize}
              onChange={setPage}
            />
          )}
        </>
      )}
    </div>
  );
}

/* 공통 부속 컴포넌트 */
function PageHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
      <p className="text-gray-600">{subtitle}</p>
    </div>
  );
}

function LoadingCard({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center p-12 bg-white rounded-lg border border-gray-200">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4" />
        <p className="text-gray-600">{label}</p>
      </div>
    </div>
  );
}

function ErrorCard({ error }: { error: unknown }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
      <h3 className="text-sm font-medium text-red-800">데이터를 불러오는데 실패했습니다.</h3>
      <p className="mt-1 text-sm text-red-700">
        {error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.'}
      </p>
    </div>
  );
}

function FetchingOverlay() {
  return (
    <div className="absolute inset-0 bg-transparent pointer-events-none flex items-center justify-center z-10 rounded-lg">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mx-auto mb-3" />
        <p className="text-gray-600 text-sm">불러오는 중...</p>
      </div>
    </div>
  );
}

function PaginationBar({
  page, totalPages, totalElements, pageSize, onChange,
}: {
  page: number;
  totalPages: number;
  totalElements: number;
  pageSize: number;
  onChange: (p: number) => void;
}) {
  return (
    <div className="flex items-center justify-center gap-2">
      <button
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        이전
      </button>
      <div className="flex gap-1">
        {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
          let pageNumber;
          if (totalPages <= 5) {
            pageNumber = i + 1;
          } else if (page <= 3) {
            pageNumber = i + 1;
          } else if (page >= totalPages - 2) {
            pageNumber = totalPages - 4 + i;
          } else {
            pageNumber = page - 2 + i;
          }
          return (
            <button
              key={pageNumber}
              onClick={() => onChange(pageNumber)}
              className={`px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                page === pageNumber
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
              }`}
            >
              {pageNumber}
            </button>
          );
        })}
      </div>
      <button
        onClick={() => onChange(page + 1)}
        disabled={page === totalPages}
        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        다음
      </button>
      <span className="ml-4 text-sm text-gray-700">
        {totalElements}건 중 {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, totalElements)}건
      </span>
    </div>
  );
}
